import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Breadcrumbs,
  Link,
  Grid,
  Skeleton,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  LinearProgress,
  Tooltip,
  IconButton,
  FormControlLabel,
  Checkbox,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LinkIcon from '@mui/icons-material/Link';
import LaunchIcon from '@mui/icons-material/Launch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoIcon from '@mui/icons-material/Info';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SourceIcon from '@mui/icons-material/Source';
import PublicIcon from '@mui/icons-material/Public';
import { companiesAPI, personsAPI, documentsAPI } from '../services/api';
import { toast } from 'react-toastify';
import CompanyEditDialog from '../components/CompanyEditDialog';
import DocumentPreviewDialog from '../components/DocumentPreviewDialog';
import { usePermissions } from '../hooks/useAuth';
import CompanyHeader from './Company/CompanyHeader';
import CompanyOwnershipGraph from './Company/CompanyOwnershipGraph';
import CompanyDetailsSection from './Company/CompanyDetailsSection';
import CompanyShareholdersSection from './Company/CompanyShareholdersSection';
import CompanyDirectorsSection from './Company/CompanyDirectorsSection';
import CompanySubsidiariesSection from './Company/CompanySubsidiariesSection';
import CompanyDocumentsSection from './Company/CompanyDocumentsSection';
import { COMPANY_DOCUMENT_SECTIONS } from './Company/companyDocumentSections';
import { getFileTypeIcon } from '../utils/documentUtils';

const DEFAULT_DOCUMENT_TYPE = 'OTHER';
const buildUploadForm = (documentType: string = DEFAULT_DOCUMENT_TYPE) => ({
  documentType,
  tags: [],
  description: '',
  documentDate: '',
  expiryDate: '',
  reportingMonth: '',
  fiscalYear: '',
});

const toCurrentReportingMonth = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  return `${year}-${month}`;
};

const Company: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fixedPanelTop = { xs: '56px', sm: '64px' };
  const fixedPanelHeight = { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' };
  const mobileDialogActionsSx = isMobile
    ? {
        flexDirection: 'column',
        gap: 1,
        px: 2,
        pb: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
        '& > .MuiButton-root': { width: '100%', minHeight: 44, ml: '0 !important' },
      }
    : undefined;
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<any | null>(null);
  const [graph, setGraph] = useState<any | null>(null);
  const [allPersons, setAllPersons] = useState<any[]>([]);
  const [allCompanies, setAllCompanies] = useState<any[]>([]);
  const [directorsApi, setDirectorsApi] = useState<Array<{ id?: string; personId: string; name: string; roleTitle?: string; isNominee?: boolean }>>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [jurisdictions, setJurisdictions] = useState<Array<{ jurisdiction: string; count: number }>>([]);
  const [enhanceOpen, setEnhanceOpen] = useState(false);
  const [enhanceLoading, setEnhanceLoading] = useState(false);
  const [enhanceJobId, setEnhanceJobId] = useState<string | null>(null);
  const [enhanceData, setEnhanceData] = useState<any | null>(null);
  const [applySelection, setApplySelection] = useState<Record<string, any>>({});
  // Directors validation state
  const [validateOpen, setValidateOpen] = useState(false);
  const [validateLoading, setValidateLoading] = useState(false);
  const [validateResult, setValidateResult] = useState<any | null>(null);
  // CRUD UI state
  const [directorDialogOpen, setDirectorDialogOpen] = useState(false);
  const [directorForm, setDirectorForm] = useState<any>({ personName: '', roleTitle: '', isNominee: false, appointmentDate: '', resignationDate: '', notes: '' });
  const [shareholderDialogOpen, setShareholderDialogOpen] = useState(false);
  const [editingOwnershipId, setEditingOwnershipId] = useState<string | null>(null);
  const [shareholderForm, setShareholderForm] = useState<any>({ ownerType: 'PERSON', ownerPersonName: '', ownerPersonId: '', ownerCompanyName: '', ownerCompanyId: '', ownershipPercentage: '', numberOfSharesHeld: '', sharesHeldRaw: '', ownershipType: 'DIRECT', isNomineeArrangement: false, nomineeDetails: '', notes: '' });
  // Documents state
  const [documents, setDocuments] = useState<any[]>([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadForm, setUploadForm] = useState<any>(buildUploadForm());
  const [isDragging, setIsDragging] = useState(false);
  const [editDocumentDialogOpen, setEditDocumentDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<any>(null);
  const [financialStatusPeriodType, setFinancialStatusPeriodType] = useState<'MONTH' | 'FISCAL_YEAR'>(
    'MONTH',
  );
  const [financialStatusReportingMonth, setFinancialStatusReportingMonth] = useState(
    toCurrentReportingMonth(),
  );
  const [financialStatusFiscalYear, setFinancialStatusFiscalYear] = useState(
    new Date().getFullYear(),
  );
  const [financialStatus, setFinancialStatus] = useState<
    'UPLOADED' | 'MISSING' | 'LATE' | 'UNKNOWN' | undefined
  >();
  const [financialStatusDueAt, setFinancialStatusDueAt] = useState<string | undefined>();
  const [financialStatusLoading, setFinancialStatusLoading] = useState(true);

  // Document preview state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
  const [previewDocumentIndex, setPreviewDocumentIndex] = useState<number>(0);

  const getFinancialStatusQuery = () => ({
    periodType: financialStatusPeriodType,
    reportingMonth:
      financialStatusPeriodType === 'MONTH' ? financialStatusReportingMonth : undefined,
    fiscalYear: financialStatusPeriodType === 'FISCAL_YEAR' ? financialStatusFiscalYear : undefined,
  });

  const refreshFinancialStatementStatus = async (companyId: string) => {
    try {
      setFinancialStatusLoading(true);
      const response = await companiesAPI.getFinancialStatementStatus(
        companyId,
        getFinancialStatusQuery(),
      );
      setFinancialStatus(response.data?.status);
      setFinancialStatusDueAt(response.data?.period?.dueAt);
    } catch (error) {
      console.error('Failed to load financial statement status:', error);
      setFinancialStatus('UNKNOWN');
      setFinancialStatusDueAt(undefined);
    } finally {
      setFinancialStatusLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [cRes, gRes, pRes, jRes, dRes, cList, docsRes] = await Promise.all([
          companiesAPI.getById(String(id)),
          companiesAPI.getCompanyOwnershipGraph(String(id)),
          personsAPI.getAll({ page: 1, limit: 5000 }),
          companiesAPI.getJurisdictions(),
          companiesAPI.getDirectors(String(id)),
          companiesAPI.getAll({ page: 1, limit: 5000 }),
          documentsAPI.getByCompany(String(id)),
        ]);
        setCompany(cRes.data);
        setGraph(gRes.data);
        const persons = pRes.data?.data || pRes.data?.items || pRes.data || [];
        setAllPersons(persons);
        setJurisdictions(jRes.data || []);
        const d = Array.isArray(dRes.data) ? dRes.data : [];
        setDirectorsApi(d.map((x: any) => ({ id: x.id, personId: x.personId, name: x.name, roleTitle: x.roleTitle, isNominee: x.isNominee })));
        const companiesList = cList.data?.data || cList.data?.items || cList.data || [];
        setAllCompanies(companiesList);
        const docs = docsRes.data?.data || docsRes.data || [];
        setDocuments(docs);
        await refreshFinancialStatementStatus(String(id));
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id) return;
    refreshFinancialStatementStatus(String(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, financialStatusPeriodType, financialStatusReportingMonth, financialStatusFiscalYear]);

  const openEdit = () => {
    if (!company) return;
    setEditOpen(true);
  };

  const openEnhance = async () => {
    if (!id) return;
    try {
      setEnhanceOpen(true);
      setEnhanceLoading(true);
      const res = await companiesAPI.startEnrichment(String(id));
      const jobId = res.data?.jobId;
      setEnhanceJobId(jobId);
      // simple poll loop (stub)
      const started = Date.now();
      const poll = async () => {
        if (!jobId) return;
        const job = await companiesAPI.getEnrichmentJob(jobId);
        if (job.data?.status === 'complete') {
          setEnhanceData(job.data);
          setEnhanceLoading(false);
          return;
        }
        if (Date.now() - started > 8000) {
          setEnhanceLoading(false);
          toast.error('Enrichment timed out');
          return;
        }
        setTimeout(poll, 400);
      };
      poll();
    } catch (e: any) {
      setEnhanceLoading(false);
      toast.error('Failed to start enrichment');
      console.error(e);
    }
  };

  // Auto-preselect high-confidence fields when results arrive (no conflicts)
  useEffect(() => {
    if (enhanceData && Array.isArray(enhanceData.candidates)) {
      const pre: Record<string, any> = {};
      // Prefer highest-confidence candidate for each field
      const fieldBest: Record<string, { conf: number; val: any }> = {};
      enhanceData.candidates.forEach((cand: any) => {
        const conf = typeof cand.confidence === 'number' ? cand.confidence : 0;
        (cand.suggestedFields || []).forEach((sf: any) => {
          if (!sf || typeof sf.field !== 'string') return;
          const prev = fieldBest[sf.field];
          if (!prev || conf > prev.conf) {
            fieldBest[sf.field] = { conf, val: sf.newValue };
          }
        });
      });
      Object.entries(fieldBest).forEach(([field, best]) => {
        if ((best.conf || 0) >= 0.9) pre[field] = best.val;
      });
      setApplySelection(pre);
    }
  }, [enhanceData]);

  const toggleFieldApply = (field: string, newValue: any) => {
    setApplySelection((prev) => {
      const next = { ...prev } as Record<string, any>;
      if (next[field] !== undefined) {
        delete next[field];
      } else {
        next[field] = newValue;
      }
      return next;
    });
  };

  const applyEnhancement = async () => {
    if (!enhanceJobId) return;
    try {
      const selectedFields = Object.entries(applySelection).map(([field, newValue]) => ({ field, newValue }));
      await companiesAPI.applyEnrichment(enhanceJobId, selectedFields as any);
      toast.success('Applied suggestions');
      setEnhanceOpen(false);
      setApplySelection({});
      if (id) {
        const updated = await companiesAPI.getById(String(id));
        setCompany(updated.data);
      }
    } catch (e: any) {
      toast.error('Failed to apply suggestions');
      console.error(e);
    }
  };

  const handleEditSave = async () => {
    if (!id) return;
    // Reload company data after save, or navigate away if deleted
    try {
      const updated = await companiesAPI.getById(String(id));
      setCompany(updated.data);
    } catch (error: any) {
      // If 404, company was deleted - navigate to home
      if (error.response?.status === 404) {
        navigate('/');
      } else {
        console.error('Failed to reload company:', error);
      }
    }
  };

  // Document handlers
  const handleUploadDocument = async () => {
    if (!id || uploadFiles.length === 0) {
      toast.error('Please select at least one file');
      return;
    }

    try {
      let successCount = 0;
      let failCount = 0;

      // Upload files one by one
      for (const file of uploadFiles) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('title', file.name);
          formData.append('documentType', uploadForm.documentType);
          formData.append('companyId', String(id));

          // Optional metadata fields
          if (uploadForm.description) formData.append('description', uploadForm.description);
          if (uploadForm.documentDate) formData.append('documentDate', uploadForm.documentDate);
          if (uploadForm.expiryDate) formData.append('expiryDate', uploadForm.expiryDate);
          if (uploadForm.reportingMonth) formData.append('reportingMonth', uploadForm.reportingMonth);
          if (uploadForm.fiscalYear) formData.append('fiscalYear', uploadForm.fiscalYear);
          if (uploadForm.tags && uploadForm.tags.length > 0) formData.append('tags', JSON.stringify(uploadForm.tags));

          await documentsAPI.upload(formData);
          successCount++;
        } catch (e) {
          console.error(`Failed to upload ${file.name}:`, e);
          failCount++;
        }
      }

      // Show results
      if (successCount > 0) {
        toast.success(`${successCount} document${successCount > 1 ? 's' : ''} uploaded successfully`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} document${failCount > 1 ? 's' : ''} failed to upload`);
      }

      // Reload documents
      const docsRes = await documentsAPI.getByCompany(String(id));
      setDocuments(docsRes.data?.data || docsRes.data || []);
      await refreshFinancialStatementStatus(String(id));

      // Reset form
      setUploadDialogOpen(false);
      setUploadFiles([]);
      setUploadForm(buildUploadForm());
    } catch (e: any) {
      toast.error('Failed to upload documents');
      console.error(e);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    setUploadFiles(prev => [...prev, ...files]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setUploadFiles(prev => [...prev, ...files]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDownloadDocument = async (docId: string, filename: string) => {
    try {
      const response = await documentsAPI.download(docId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error('Failed to download document');
      console.error(e);
    }
  };

  const handlePreviewDocument = (doc: any, index: number) => {
    // Store current document for metadata access
    setEditingDocument(doc);
    const docIndex = index >= 0 ? index : documents.findIndex((d: any) => d.id === doc.id);

    setPreviewDocumentId(doc.id);
    setPreviewDocumentIndex(docIndex >= 0 ? docIndex : 0);
    setPreviewDialogOpen(true);
  };

  const handlePreviewNavigate = (index: number) => {
    if (index >= 0 && index < documents.length) {
      setPreviewDocumentId(documents[index].id);
      setPreviewDocumentIndex(index);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!window.confirm('Delete this document? This action cannot be undone.')) return;

    try {
      await documentsAPI.delete(docId);
      toast.success('Document deleted');

      // Reload documents
      const docsRes = await documentsAPI.getByCompany(String(id));
      setDocuments(docsRes.data?.data || docsRes.data || []);
      await refreshFinancialStatementStatus(String(id));
    } catch (e: any) {
      toast.error('Failed to delete document');
      console.error(e);
    }
  };

  const handleOpenEditDocument = (doc: any) => {
    setEditingDocument({
      id: doc.id,
      title: doc.title,
      description: doc.description || '',
      documentType: doc.documentType,
      tags: doc.tags || [],
      documentDate: doc.documentDate || '',
      expiryDate: doc.expiryDate || '',
      reportingMonth: doc.reportingMonth || '',
      fiscalYear: doc.fiscalYear || '',
    });
    setEditDocumentDialogOpen(true);
  };

  const handleSaveDocumentEdit = async () => {
    if (!editingDocument || !id) return;

    try {
      await documentsAPI.update(editingDocument.id, {
        title: editingDocument.title,
        description: editingDocument.description,
        documentType: editingDocument.documentType,
        tags: editingDocument.tags,
        documentDate: editingDocument.documentDate,
        expiryDate: editingDocument.expiryDate,
        reportingMonth: editingDocument.reportingMonth || undefined,
        fiscalYear: editingDocument.fiscalYear || undefined,
      });
      toast.success('Document updated');

      // Reload documents
      const docsRes = await documentsAPI.getByCompany(String(id));
      setDocuments(docsRes.data?.data || docsRes.data || []);
      await refreshFinancialStatementStatus(String(id));

      // Close dialog
      setEditDocumentDialogOpen(false);
      setEditingDocument(null);
    } catch (e: any) {
      toast.error('Failed to update document');
      console.error(e);
    }
  };

  const parentCompanies = useMemo(() => {
    if (!graph) return [] as Array<{ node: any; edge: any }>;
    const incoming = (graph.edges || []).filter((e: any) => e.target === id);
    const nodeById: Record<string, any> = {};
    (graph.nodes || []).forEach((n: any) => (nodeById[n.id] = n));
    return incoming.map((e: any) => ({ node: nodeById[e.source] || nodeById[String(e.source).replace('person-', '')], edge: e }));
  }, [graph, id]);

  const subsidiaries = useMemo(() => {
    if (!graph) return [] as any[];
    const outgoing = (graph.edges || []).filter((e: any) => e.source === id);
    const nodeById: Record<string, any> = {};
    (graph.nodes || []).forEach((n: any) => (nodeById[n.id] = n));
    return outgoing.map((e: any) => nodeById[e.target]);
  }, [graph, id]);

  const directors = useMemo(() => {
    // Show only persisted directors from API
    return (directorsApi || []).map((d: any) => ({
      id: String(d.personId),
      name: d.name,
      roles: ['DIRECTOR'],
      relationId: d?.id,
      roleTitle: d?.roleTitle,
      isNominee: d?.isNominee,
      profilePhotoUrl: d?.profilePhotoUrl,
    }));
  }, [directorsApi]);

  const handleRemoveDirector = async (relationId: string, name: string) => {
    if (!id) return;
    try {
      if (!window.confirm(`Remove director ${name}?`)) return;
      await companiesAPI.removeDirector(String(id), relationId);
      const dRes2 = await companiesAPI.getDirectors(String(id), {
        headers: { 'Cache-Control': 'no-cache' },
        params: { t: Date.now() },
      });
      const d2 = Array.isArray(dRes2.data) ? dRes2.data : [];
      setDirectorsApi(
        d2.map((x: any) => ({
          id: x.id,
          personId: x.personId,
          name: x.name,
          roleTitle: x.roleTitle,
          isNominee: x.isNominee,
        }))
      );
      toast.success('Director removed');
    } catch (err) {
      toast.error('Failed to remove director');
    }
  };

  const handleEditShareholder = async (params: { ownerId: string; isPerson: boolean; label: string }) => {
    if (!id) return;
    try {
      const ownRes = await companiesAPI.getOwnerships(String(id), {
        headers: { 'Cache-Control': 'no-cache' },
        params: { t: Date.now() },
      });
      const list = Array.isArray(ownRes.data) ? ownRes.data : [];
      const match = list.find((o: any) => String(o.ownerId) === params.ownerId);
      if (!match) return;
      setEditingOwnershipId(match.id);
      setShareholderForm({
        ownerType: params.isPerson ? 'PERSON' : 'COMPANY',
        ownerPersonId: params.isPerson ? params.ownerId : '',
        ownerPersonName: params.isPerson ? params.label : '',
        ownerCompanyId: !params.isPerson ? params.ownerId : '',
        ownerCompanyName: !params.isPerson ? params.label : '',
        ownershipPercentage: match.ownershipPercentage || '',
        numberOfSharesHeld: match.numberOfSharesHeld || '',
        sharesHeldRaw: match.sharesHeldRaw || '',
        ownershipType: match.ownershipType || 'DIRECT',
        isNomineeArrangement: match.isNomineeArrangement || false,
        nomineeDetails: match.nomineeDetails || '',
        notes: match.notes || '',
      });
      setShareholderDialogOpen(true);
    } catch (err) {
      toast.error('Failed to load shareholder data');
    }
  };

  const handleRemoveShareholder = async (params: { ownerId: string; isPerson: boolean; label: string }) => {
    if (!id) return;
    try {
      const ownRes = await companiesAPI.getOwnerships(String(id), {
        headers: { 'Cache-Control': 'no-cache' },
        params: { t: Date.now() },
      });
      const list = Array.isArray(ownRes.data) ? ownRes.data : [];
      const match = list.find((o: any) => String(o.ownerId) === params.ownerId);
      if (!match) return;
      if (!window.confirm(`Remove shareholder ${params.label}?`)) return;
      await companiesAPI.removeOwnership(String(id), match.id);
      const gRes = await companiesAPI.getCompanyOwnershipGraph(String(id));
      setGraph(gRes.data);
      toast.success('Shareholder removed');
    } catch (err) {
      toast.error('Failed to remove shareholder');
    }
  };

  const handleDownloadPdf = async () => {
    if (!id || !company) return;
    try {
      const response = await companiesAPI.downloadReport(String(id));
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${String(company.name).replace(/[^a-zA-Z0-9]/g, '_')}_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded successfully');
    } catch (e) {
      toast.error('Failed to download report');
    }
  };

  const handleValidate = async () => {
    if (!id) return;
    try {
      setValidateOpen(true);
      setValidateLoading(true);
      const res = await companiesAPI.validateDirectors(String(id));
      setValidateResult(res.data);
    } catch (e) {
      toast.error('Failed to validate directors');
    } finally {
      setValidateLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: 4, px: { xs: 2, sm: 3 } }}>
      <Breadcrumbs
        sx={{
          mb: 2,
          '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
          '& .MuiBreadcrumbs-li': { minWidth: 0 },
        }}
      >
        <Link component={RouterLink} to="/" underline="hover" color="inherit">
          Dashboard
        </Link>
        <Link component={RouterLink} to="/companies" underline="hover" color="inherit">
          Companies
        </Link>
        <Typography color="text.primary" noWrap sx={{ maxWidth: { xs: 160, sm: 420 } }}>{company?.name ?? 'Company'}</Typography>
      </Breadcrumbs>

      {loading ? (
        <Paper sx={{ p: 3 }}>
          <Skeleton variant="rectangular" height={160} />
        </Paper>
      ) : company ? (
        <>
          <CompanyHeader
            company={company}
            directorsCount={directorsApi.length}
            shareholdersCount={parentCompanies.length}
            onEdit={openEdit}
            onEnhance={openEnhance}
            onValidate={handleValidate}
            onAddDirector={() => setDirectorDialogOpen(true)}
            onAddShareholder={() => setShareholderDialogOpen(true)}
            onDownloadPdf={handleDownloadPdf}
            hasPermission={hasPermission}
          />
          <CompanyOwnershipGraph companyId={id} />
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} md={4}>
              <CompanyDetailsSection company={company} />
            </Grid>
            <Grid item xs={12} md={4}>
              <CompanyShareholdersSection
                parentCompanies={parentCompanies}
                onEditShareholder={handleEditShareholder}
                onRemoveShareholder={handleRemoveShareholder}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CompanyDirectorsSection
                directors={directors}
                onRemoveDirector={handleRemoveDirector}
              />
            </Grid>
            <Grid item xs={12}>
              <CompanySubsidiariesSection subsidiaries={subsidiaries} />
            </Grid>
            <Grid item xs={12}>
              <CompanyDocumentsSection
                documents={documents}
                onUploadClick={(documentType: string) => {
                  const validDocumentType =
                    COMPANY_DOCUMENT_SECTIONS.find((section) => section.defaultDocumentType === documentType)
                      ?.defaultDocumentType || documentType || DEFAULT_DOCUMENT_TYPE;
                  setUploadFiles([]);
                  setUploadForm(buildUploadForm(validDocumentType));
                  setUploadDialogOpen(true);
                }}
                onPreview={handlePreviewDocument}
                onEdit={handleOpenEditDocument}
                onDownload={handleDownloadDocument}
                onDelete={handleDeleteDocument}
                hasPermission={hasPermission}
                financialStatementStatus={{
                  periodType: financialStatusPeriodType,
                  reportingMonth: financialStatusReportingMonth,
                  fiscalYear: financialStatusFiscalYear,
                  status: financialStatus,
                  dueAt: financialStatusDueAt,
                  loading: financialStatusLoading,
                }}
                onFinancialPeriodTypeChange={(periodType) => setFinancialStatusPeriodType(periodType)}
                onFinancialReportingMonthChange={(reportingMonth) =>
                  setFinancialStatusReportingMonth(reportingMonth)
                }
                onFinancialFiscalYearChange={(fiscalYear) => {
                  if (Number.isNaN(fiscalYear)) return;
                  setFinancialStatusFiscalYear(fiscalYear);
                }}
              />
            </Grid>
          </Grid>
        </>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Alert severity="warning">Company not found.</Alert>
        </Paper>
      )}

      {/* Edit Dialog */}
      <CompanyEditDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        company={company}
        onSave={handleEditSave}
        jurisdictions={(jurisdictions || []).map((j) => j.jurisdiction).filter(Boolean)}
        showAdvancedFields={false}
      />

      {/* Add Director Dialog */}
      <Dialog open={directorDialogOpen} onClose={() => setDirectorDialogOpen(false)} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle sx={isMobile ? { px: 2, py: 1.5 } : undefined}>Add Director</DialogTitle>
        <DialogContent sx={isMobile ? { px: 2 } : undefined}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={(allPersons || []).map((p) => ({ label: p.name, id: p.id }))}
              onChange={(e, value) => setDirectorForm({ ...directorForm, personId: value?.id || '', personName: value?.label || '' })}
              renderInput={(params) => (
                <TextField {...params} label="Person" placeholder="Search or type to create" />
              )}
            />
            <TextField
              label="Or new person name"
              value={directorForm.personName}
              onChange={(e) => setDirectorForm({ ...directorForm, personName: e.target.value, personId: '' })}
              fullWidth
            />
            <TextField
              label="Role Title"
              value={directorForm.roleTitle}
              onChange={(e) => setDirectorForm({ ...directorForm, roleTitle: e.target.value })}
              placeholder="e.g., Director, Managing Director"
              fullWidth
            />
            <FormControlLabel
              control={<Checkbox checked={!!directorForm.isNominee} onChange={(e) => setDirectorForm({ ...directorForm, isNominee: e.target.checked })} />}
              label="Nominee"
            />
            <TextField
              label="Appointment Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={directorForm.appointmentDate}
              onChange={(e) => setDirectorForm({ ...directorForm, appointmentDate: e.target.value })}
              fullWidth
            />
            <TextField
              label="Resignation Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={directorForm.resignationDate}
              onChange={(e) => setDirectorForm({ ...directorForm, resignationDate: e.target.value })}
              fullWidth
            />
            <TextField
              label="Notes"
              value={directorForm.notes}
              onChange={(e) => setDirectorForm({ ...directorForm, notes: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={mobileDialogActionsSx}>
          <Button onClick={() => setDirectorDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!id) return;
              try {
                await companiesAPI.addDirector(String(id), {
                  personId: directorForm.personId || undefined,
                  personName: directorForm.personId ? undefined : (directorForm.personName || undefined),
                  roleTitle: directorForm.roleTitle,
                  isNominee: !!directorForm.isNominee,
                  appointmentDate: directorForm.appointmentDate || undefined,
                  resignationDate: directorForm.resignationDate || undefined,
                  notes: directorForm.notes,
                });
                toast.success('Director added');
                const dRes = await companiesAPI.getDirectors(String(id));
                const d = Array.isArray(dRes.data) ? dRes.data : [];
                setDirectorsApi(d.map((x: any) => ({ id: x.id, personId: x.personId, name: x.name, roleTitle: x.roleTitle, isNominee: x.isNominee })));
                setDirectorDialogOpen(false);
                setDirectorForm({ personName: '', roleTitle: '', isNominee: false, appointmentDate: '', resignationDate: '', notes: '' });
              } catch (e: any) {
                toast.error('Failed to add director');
                console.error(e);
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Shareholder Dialog */}
      <Dialog open={shareholderDialogOpen} onClose={() => { setShareholderDialogOpen(false); setEditingOwnershipId(null); }} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle sx={isMobile ? { px: 2, py: 1.5 } : undefined}>{editingOwnershipId ? 'Edit Shareholder Type' : 'Add Shareholder'}</DialogTitle>
        <DialogContent sx={isMobile ? { px: 2 } : undefined}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Select value={shareholderForm.ownerType} onChange={(e) => setShareholderForm({ ...shareholderForm, ownerType: e.target.value })} fullWidth>
              <MenuItem value="PERSON">Person</MenuItem>
              <MenuItem value="COMPANY">Company</MenuItem>
            </Select>
            {shareholderForm.ownerType === 'PERSON' ? (
              <>
                <Autocomplete
                  options={(allPersons || []).map((p) => ({ label: p.name, id: p.id }))}
                  onChange={(e, value) => setShareholderForm({ ...shareholderForm, ownerPersonId: value?.id || '', ownerPersonName: value?.label || '' })}
                  renderInput={(params) => (
                    <TextField {...params} label="Person" placeholder="Search or type to create" />
                  )}
                />
                <TextField
                  label="Or new person name"
                  value={shareholderForm.ownerPersonName}
                  onChange={(e) => setShareholderForm({ ...shareholderForm, ownerPersonName: e.target.value, ownerPersonId: '' })}
                  fullWidth
                />
              </>
            ) : (
              <>
                <Autocomplete
                  options={(allCompanies || []).map((c) => ({ label: c.name, id: c.id }))}
                  onChange={(e, value) => setShareholderForm({ ...shareholderForm, ownerCompanyId: value?.id || '', ownerCompanyName: value?.label || '' })}
                  renderInput={(params) => (
                    <TextField {...params} label="Company" placeholder="Search or type to create" />
                  )}
                />
                <TextField
                  label="Or new company name"
                  value={shareholderForm.ownerCompanyName}
                  onChange={(e) => setShareholderForm({ ...shareholderForm, ownerCompanyName: e.target.value, ownerCompanyId: '' })}
                  fullWidth
                />
              </>
            )}
            <TextField
              label="Ownership %"
              value={shareholderForm.ownershipPercentage}
              onChange={(e) => setShareholderForm({ ...shareholderForm, ownershipPercentage: e.target.value })}
              type="number"
              fullWidth
            />
            <TextField
              label="Number of Shares"
              value={shareholderForm.numberOfSharesHeld}
              onChange={(e) => setShareholderForm({ ...shareholderForm, numberOfSharesHeld: e.target.value })}
              type="number"
              fullWidth
            />
            <TextField
              label="Shares Held (raw)"
              value={shareholderForm.sharesHeldRaw}
              onChange={(e) => setShareholderForm({ ...shareholderForm, sharesHeldRaw: e.target.value })}
              fullWidth
            />
            <TextField
              label="Ownership Type"
              value={shareholderForm.ownershipType}
              onChange={(e) => setShareholderForm({ ...shareholderForm, ownershipType: e.target.value })}
              placeholder="e.g., DIRECT, INDIRECT"
              fullWidth
            />
            <FormControlLabel
              control={<Checkbox checked={!!shareholderForm.isNomineeArrangement} onChange={(e) => setShareholderForm({ ...shareholderForm, isNomineeArrangement: e.target.checked })} />}
              label="Nominee arrangement"
            />
            <TextField
              label="Nominee Details"
              value={shareholderForm.nomineeDetails}
              onChange={(e) => setShareholderForm({ ...shareholderForm, nomineeDetails: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            <TextField
              label="Notes"
              value={shareholderForm.notes}
              onChange={(e) => setShareholderForm({ ...shareholderForm, notes: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={mobileDialogActionsSx}>
          <Button onClick={() => { setShareholderDialogOpen(false); setEditingOwnershipId(null); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!id) return;
              try {
                const payload: any = {
                  ownerType: shareholderForm.ownerType,
                  ownershipPercentage: shareholderForm.ownershipPercentage ? parseFloat(shareholderForm.ownershipPercentage) : undefined,
                  numberOfSharesHeld: shareholderForm.numberOfSharesHeld ? parseFloat(shareholderForm.numberOfSharesHeld) : undefined,
                  sharesHeldRaw: shareholderForm.sharesHeldRaw || undefined,
                  ownershipType: shareholderForm.ownershipType || undefined,
                  isNomineeArrangement: !!shareholderForm.isNomineeArrangement,
                  nomineeDetails: shareholderForm.nomineeDetails || undefined,
                  notes: shareholderForm.notes || undefined,
                };
                if (shareholderForm.ownerType === 'PERSON') {
                  payload.ownerPersonId = shareholderForm.ownerPersonId || undefined;
                  payload.ownerPersonName = shareholderForm.ownerPersonId ? undefined : (shareholderForm.ownerPersonName || undefined);
                } else {
                  payload.ownerCompanyId = shareholderForm.ownerCompanyId || undefined;
                  payload.ownerCompanyName = shareholderForm.ownerCompanyId ? undefined : (shareholderForm.ownerCompanyName || undefined);
                }

                if (editingOwnershipId) {
                  // Update existing ownership
                  await companiesAPI.updateOwnership(String(id), editingOwnershipId, payload);
                  toast.success('Shareholder updated');
                } else {
                  // Add new ownership
                  await companiesAPI.addOwnership(String(id), payload);
                  toast.success('Shareholder added');
                }
                const gRes = await companiesAPI.getCompanyOwnershipGraph(String(id));
                setGraph(gRes.data);
                setShareholderDialogOpen(false);
                setEditingOwnershipId(null);
                setShareholderForm({ ownerType: 'PERSON', ownerPersonName: '', ownerPersonId: '', ownerCompanyName: '', ownerCompanyId: '', ownershipPercentage: '', numberOfSharesHeld: '', sharesHeldRaw: '', ownershipType: 'DIRECT', isNomineeArrangement: false, nomineeDetails: '', notes: '' });
              } catch (e: any) {
                toast.error('Failed to add shareholder');
                console.error(e);
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} fullWidth maxWidth="md" fullScreen={isMobile}>
        <DialogTitle sx={isMobile ? { px: 2, py: 1.5 } : undefined}>Upload Documents</DialogTitle>
        <DialogContent sx={isMobile ? { px: 2 } : undefined}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Drag and Drop Zone */}
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: { xs: 2.5, sm: 4 },
                textAlign: 'center',
                bgcolor: isDragging ? 'action.hover' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: 'primary.light',
                  bgcolor: 'action.hover',
                },
              }}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <UploadFileIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                {isDragging ? 'Drop files here' : 'Drag & drop files here'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                or click to select files
              </Typography>
              <input
                id="file-input"
                type="file"
                hidden
                multiple
                onChange={handleFileSelect}
              />
            </Box>

            {/* Selected Files List */}
            {uploadFiles.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Files ({uploadFiles.length})
                </Typography>
                <List dense>
                  {uploadFiles.map((file, index) => (
                    <Box key={index} display="flex" alignItems="center" gap={1} py={0.75} minWidth={0}>
                      {getFileTypeIcon(file.name)}
                      <Box flex={1} minWidth={0}>
                        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{file.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(file.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>
                      <IconButton
                        aria-label="Remove file"
                        size="small"
                        onClick={() => handleRemoveFile(index)}
                        color="error"
                        sx={{ minWidth: 40, minHeight: 40 }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </List>
              </Box>
            )}

            {/* Document Type Selection */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Document Type
              </Typography>
              <Select
                value={uploadForm.documentType}
                onChange={(e) => setUploadForm({ ...uploadForm, documentType: e.target.value })}
                fullWidth
                size="small"
              >
                <MenuItem value="INCORPORATION">Incorporation</MenuItem>
                <MenuItem value="SHAREHOLDER_REGISTER">Shareholder Register</MenuItem>
                <MenuItem value="BOARD_RESOLUTION">Board Resolution</MenuItem>
                <MenuItem value="FILING">Filing</MenuItem>
                <MenuItem value="FINANCIAL_STATEMENT">Financial Statement</MenuItem>
                <MenuItem value="CONTRACT">Contract</MenuItem>
                <MenuItem value="CERTIFICATE">Certificate</MenuItem>
                <MenuItem value="CORRESPONDENCE">Correspondence</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </Box>

            {/* Description */}
            <TextField
              label="Description (Optional)"
              value={uploadForm.description || ''}
              onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
              placeholder="Add a description for these documents"
            />

            {/* Document Date */}
            <TextField
              label="Document Date (Optional)"
              type="date"
              value={uploadForm.documentDate || ''}
              onChange={(e) => setUploadForm({ ...uploadForm, documentDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="The date on the document itself"
            />

            {/* Expiry Date */}
            <TextField
              label="Expiry Date (Optional)"
              type="date"
              value={uploadForm.expiryDate || ''}
              onChange={(e) => setUploadForm({ ...uploadForm, expiryDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="When this document expires or needs renewal"
            />
            <TextField
              label="Calendar Month (Optional)"
              type="month"
              value={uploadForm.reportingMonth || ''}
              onChange={(e) => setUploadForm({ ...uploadForm, reportingMonth: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Accounting period month (YYYY-MM)"
            />
            <TextField
              label="Fiscal Year (Optional)"
              type="number"
              value={uploadForm.fiscalYear || ''}
              onChange={(e) => setUploadForm({ ...uploadForm, fiscalYear: e.target.value })}
              fullWidth
              helperText="Fiscal year used for reporting"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={mobileDialogActionsSx}>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            setUploadFiles([]);
            setUploadForm(buildUploadForm());
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadDocument}
            disabled={uploadFiles.length === 0}
            startIcon={<UploadFileIcon />}
          >
            Upload {uploadFiles.length > 0 && `(${uploadFiles.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={editDocumentDialogOpen} onClose={() => setEditDocumentDialogOpen(false)} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle sx={isMobile ? { px: 2, py: 1.5 } : undefined}>Edit Document</DialogTitle>
        <DialogContent sx={isMobile ? { px: 2 } : undefined}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              value={editingDocument?.title || ''}
              onChange={(e) => setEditingDocument({ ...editingDocument, title: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={editingDocument?.description || ''}
              onChange={(e) => setEditingDocument({ ...editingDocument, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Document Type
              </Typography>
              <Select
                value={editingDocument?.documentType || 'OTHER'}
                onChange={(e) => setEditingDocument({ ...editingDocument, documentType: e.target.value })}
                fullWidth
                size="small"
              >
                <MenuItem value="INCORPORATION">Incorporation</MenuItem>
                <MenuItem value="SHAREHOLDER_REGISTER">Shareholder Register</MenuItem>
                <MenuItem value="BOARD_RESOLUTION">Board Resolution</MenuItem>
                <MenuItem value="FILING">Filing</MenuItem>
                <MenuItem value="FINANCIAL_STATEMENT">Financial Statement</MenuItem>
                <MenuItem value="CONTRACT">Contract</MenuItem>
                <MenuItem value="CERTIFICATE">Certificate</MenuItem>
                <MenuItem value="CORRESPONDENCE">Correspondence</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </Box>

            {/* Document Date */}
            <TextField
              label="Document Date (Optional)"
              type="date"
              value={editingDocument?.documentDate ? new Date(editingDocument.documentDate).toISOString().split('T')[0] : ''}
              onChange={(e) => setEditingDocument({ ...editingDocument, documentDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="The date on the document itself"
            />

            {/* Expiry Date */}
            <TextField
              label="Expiry Date (Optional)"
              type="date"
              value={editingDocument?.expiryDate ? new Date(editingDocument.expiryDate).toISOString().split('T')[0] : ''}
              onChange={(e) => setEditingDocument({ ...editingDocument, expiryDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="When this document expires or needs renewal"
            />
            <TextField
              label="Calendar Month (Optional)"
              type="month"
              value={editingDocument?.reportingMonth || ''}
              onChange={(e) => setEditingDocument({ ...editingDocument, reportingMonth: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Accounting period month (YYYY-MM)"
            />
            <TextField
              label="Fiscal Year (Optional)"
              type="number"
              value={editingDocument?.fiscalYear || ''}
              onChange={(e) => setEditingDocument({ ...editingDocument, fiscalYear: e.target.value })}
              fullWidth
              helperText="Fiscal year used for reporting"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={mobileDialogActionsSx}>
          <Button onClick={() => {
            setEditDocumentDialogOpen(false);
            setEditingDocument(null);
          }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveDocumentEdit}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhancement Drawer - Using Box instead of Drawer to avoid z-index issues */}
      {enhanceOpen && (
        <>
          {/* Backdrop */}
          <Box
            onClick={() => setEnhanceOpen(false)}
            sx={{
              position: 'fixed',
              top: fixedPanelTop,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
            }}
          />
          {/* Drawer Content */}
          <Box
            sx={{
              position: 'fixed',
              top: fixedPanelTop,
              right: 0,
              left: { xs: 0, sm: 'auto' },
              width: { xs: '100%', sm: 420 },
              height: fixedPanelHeight,
              backgroundColor: 'background.paper',
              boxShadow: 24,
              zIndex: 1001,
              overflowY: 'auto',
            }}
          >
            <Box sx={{ p: { xs: 2, sm: 2 }, pt: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon color="primary" />
            Enhance Company
          </Typography>
          {enhanceLoading && <LinearProgress sx={{ mb: 2 }} />}
          {!enhanceLoading && enhanceData && Array.isArray(enhanceData.candidates) && enhanceData.candidates.length > 0 ? (
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {enhanceData.candidates.map((cand: any) => (
                <Paper key={cand.candidateId} sx={{ p: 1.5, mb: 1.5 }} elevation={2}>
                  {/* Header with provider and confidence */}
                   <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} sx={{ mb: 1 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2">
                        {cand.providerAttribution?.url ? (
                          <Link href={cand.providerAttribution.url} target="_blank" rel="noreferrer">
                            {cand.providerAttribution?.name || 'Provider'}
                          </Link>
                        ) : (
                          cand.providerAttribution?.name || 'Provider'
                        )}
                      </Typography>
                      {cand.hasGovernmentSource && (
                        <Tooltip title="Verified by government registry">
                          <VerifiedUserIcon color="success" sx={{ fontSize: 16 }} />
                        </Tooltip>
                      )}
                    </Box>
                    <Tooltip title={cand.confidenceExplanation || 'Confidence score'}>
                      <Chip 
                        size="small" 
                        label={`${Math.round((cand.confidence || 0) * 100)}%`} 
                        color={cand.confidence >= 0.9 ? 'success' : cand.confidence >= 0.7 ? 'warning' : 'default'}
                        icon={<InfoIcon sx={{ fontSize: 14 }} />}
                      />
                    </Tooltip>
                  </Box>

                  {/* Confidence explanation */}
                  {cand.confidenceExplanation && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
                      {cand.confidenceExplanation}
                    </Typography>
                  )}

                  {/* Notes/caveats */}
                  {cand.notes && (
                    <Alert severity="info" sx={{ mb: 1, py: 0 }} icon={<InfoIcon sx={{ fontSize: 16 }} />}>
                      <Typography variant="caption">{cand.notes}</Typography>
                    </Alert>
                  )}

                  {/* Sources */}
                  {Array.isArray(cand.sources) && cand.sources.length > 0 && (
                    <Accordion sx={{ mb: 1, '&:before': { display: 'none' } }} elevation={0}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 32, py: 0, px: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <SourceIcon sx={{ fontSize: 14 }} />
                          <Typography variant="caption" fontWeight={500}>
                            {cand.sources.length} source{cand.sources.length > 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 1 }}>
                        <List dense disablePadding>
                          {cand.sources.map((src: any, idx: number) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              {src.isGovernment ? (
                                <Tooltip title="Government registry">
                                  <AccountBalanceIcon color="success" sx={{ fontSize: 14 }} />
                                </Tooltip>
                              ) : (
                                <PublicIcon color="action" sx={{ fontSize: 14 }} />
                              )}
                              <Link
                                href={src.url}
                                target="_blank"
                                rel="noreferrer"
                               sx={{ fontSize: '0.75rem', maxWidth: { xs: 190, sm: 280 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                              >
                                {src.name || new URL(src.url).hostname}
                              </Link>
                              {src.type && (
                                <Chip size="small" label={src.type.replace('_', ' ')} sx={{ height: 16, fontSize: '0.65rem', ml: 'auto' }} />
                              )}
                            </Box>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Suggested fields */}
                  <List dense>
                    {(cand.suggestedFields || []).map((sf: any) => {
                      const preselect = (cand.confidence || 0) >= 0.9;
                      const checked = applySelection[sf.field] !== undefined || preselect;
                      return (
                        <ListItemButton key={sf.field} onClick={() => toggleFieldApply(sf.field, sf.newValue)} sx={{ borderRadius: 1 }}>
                          <ListItemText
                            primary={
                               <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                                 <Box component="span" sx={{ overflowWrap: 'anywhere' }}>{sf.field}</Box>
                                {sf.evidence === 'government_registry' && (
                                  <Tooltip title="From government registry">
                                    <VerifiedUserIcon color="success" sx={{ fontSize: 12 }} />
                                  </Tooltip>
                                )}
                              </Box>
                            }
                            secondary={`${sf.currentValue ?? '—'} → ${sf.newValue}`}
                             secondaryTypographyProps={{ sx: { fontSize: '0.75rem', overflowWrap: 'anywhere' } }}
                          />
                          <Chip size="small" color={checked ? 'success' : 'default'} label={checked ? 'Apply' : 'Skip'} icon={checked ? <CheckCircleIcon /> : undefined} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Paper>
              ))}
            </Box>
          ) : (
            !enhanceLoading && <Alert severity="info" sx={{ mt: 2 }}>No suggestions yet.</Alert>
          )}
          <Box sx={{ mt: 'auto' }}>
             <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
               <Button onClick={() => setEnhanceOpen(false)} fullWidth={isMobile}>Close</Button>
               <Button variant="contained" disabled={!enhanceData || Object.keys(applySelection).length === 0} onClick={applyEnhancement} fullWidth={isMobile}>
                Apply selected
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
        </>
      )}

      {/* Validate Directors Drawer */}
      {validateOpen && (
        <>
          {/* Backdrop */}
          <Box
            onClick={() => setValidateOpen(false)}
            sx={{
              position: 'fixed',
              top: fixedPanelTop,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
            }}
          />
          {/* Drawer Content */}
          <Box
            sx={{
              position: 'fixed',
              top: fixedPanelTop,
              right: 0,
              left: { xs: 0, sm: 'auto' },
              width: { xs: '100%', sm: 640 },
              height: fixedPanelHeight,
              backgroundColor: 'background.paper',
              boxShadow: 24,
              zIndex: 1001,
              overflowY: 'auto',
            }}
          >
            <Box sx={{ p: { xs: 2, sm: 2 }, pt: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <VerifiedUserIcon color="primary" />
                Validate Directors
              </Typography>
          {validateLoading && <LinearProgress sx={{ mb: 2 }} />}
          {!validateLoading && validateResult && validateResult.ok ? (
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {/* Summary Section */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssessmentIcon fontSize="small" />
                  Analysis Summary
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip size="small" label={`In Database: ${validateResult.summary?.currentCount ?? 0}`} />
                  <Chip size="small" label={`In Registry: ${validateResult.summary?.activeRegistryCount ?? validateResult.summary?.registryCount ?? 0}`} color="primary" />
                  <Chip size="small" label={`Verified: ${validateResult.summary?.verifiedCount ?? 0}`} color="success" />
                  {(validateResult.summary?.issuesCount ?? 0) > 0 && (
                    <Chip size="small" label={`Issues: ${validateResult.summary?.issuesCount}`} color="warning" />
                  )}
                  <Chip 
                    size="small" 
                    label={`Confidence: ${Math.round(((validateResult.summary?.confidence ?? 0) * 100))}%`}
                    color={validateResult.summary?.confidence >= 0.8 ? 'success' : validateResult.summary?.confidence >= 0.6 ? 'warning' : 'default'}
                  />
                  {validateResult.summary?.hasGovernmentSource ? (
                    <Chip size="small" color="success" label="Government Source" icon={<VerifiedUserIcon />} />
                  ) : (
                    <Chip size="small" variant="outlined" label="No gov source" />
                  )}
                </Stack>
                {validateResult.notes && (
                  <Alert severity="info" sx={{ mt: 1.5, py: 0.5 }} icon={<InfoIcon fontSize="small" />}>
                    <Typography variant="caption">{validateResult.notes}</Typography>
                  </Alert>
                )}
              </Paper>

              {/* Missing Directors - To Add */}
              <Accordion defaultExpanded={validateResult.suggestions?.toAdd?.length > 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonAddIcon color="success" />
                    <Typography variant="subtitle2">Missing Directors</Typography>
                    <Chip size="small" label={validateResult.suggestions?.toAdd?.length || 0} color={validateResult.suggestions?.toAdd?.length > 0 ? 'warning' : 'default'} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {Array.isArray(validateResult.suggestions?.toAdd) && validateResult.suggestions.toAdd.length > 0 ? (
                    <List dense disablePadding>
                      {validateResult.suggestions.toAdd.map((d: any, idx: number) => (
                        <Paper key={`add-${idx}`} sx={{ p: 1.5, mb: 1, bgcolor: 'success.50' }} variant="outlined">
                           <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                             <Box minWidth={0}>
                               <Typography variant="body2" fontWeight="bold" sx={{ overflowWrap: 'anywhere' }}>{d.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {[d.roleTitle, d.appointmentDate ? `Since ${d.appointmentDate}` : null, d.status].filter(Boolean).join(' • ')}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={0.5}>
                              {typeof d.confidence === 'number' && (
                                <Chip size="small" label={`${Math.round(d.confidence * 100)}%`} color={d.confidence >= 0.8 ? 'success' : 'warning'} />
                              )}
                              {d.hasGovernmentSource && (
                                <Tooltip title="Confirmed by government registry">
                                  <VerifiedUserIcon color="success" fontSize="small" />
                                </Tooltip>
                              )}
                            </Stack>
                          </Box>
                          {d.reason && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                              {d.reason}
                            </Typography>
                          )}
                          {Array.isArray(d.sources) && d.sources.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary">Sources:</Typography>
                              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                                {d.sources.map((s: any, si: number) => (
                                  <Chip
                                    key={si}
                                    size="small"
                                    label={s.name || new URL(s.url).hostname}
                                    component="a"
                                    href={s.url}
                                    target="_blank"
                                    clickable
                                    icon={s.isGovernment ? <VerifiedUserIcon /> : <PublicIcon />}
                                    color={s.isGovernment ? 'success' : 'default'}
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 22 }}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </List>
                  ) : (
                    <Alert severity="success" icon={<CheckCircleIcon />}>All current directors are in the database.</Alert>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Potential Removals */}
              <Accordion defaultExpanded={validateResult.suggestions?.toRemove?.length > 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DeleteOutlineIcon color="error" />
                    <Typography variant="subtitle2">Potential Removals</Typography>
                    <Chip size="small" label={validateResult.suggestions?.toRemove?.length || 0} color={validateResult.suggestions?.toRemove?.length > 0 ? 'error' : 'default'} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {Array.isArray(validateResult.suggestions?.toRemove) && validateResult.suggestions.toRemove.length > 0 ? (
                    <List dense disablePadding>
                      {validateResult.suggestions.toRemove.map((d: any, idx: number) => (
                        <Paper key={`remove-${idx}`} sx={{ p: 1.5, mb: 1, bgcolor: 'error.50' }} variant="outlined">
                           <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                             <Box minWidth={0}>
                               <Typography variant="body2" fontWeight="bold" sx={{ overflowWrap: 'anywhere' }}>{d.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {[d.roleTitle, d.appointmentDate].filter(Boolean).join(' • ')}
                              </Typography>
                            </Box>
                            {typeof d.confidence === 'number' && (
                              <Chip size="small" label={`${Math.round(d.confidence * 100)}% conf`} variant="outlined" />
                            )}
                          </Box>
                          {d.reason && (
                            <Alert severity="warning" sx={{ mt: 1, py: 0 }} icon={<InfoIcon fontSize="small" />}>
                              <Typography variant="caption">{d.reason}</Typography>
                            </Alert>
                          )}
                        </Paper>
                      ))}
                    </List>
                  ) : (
                    <Alert severity="success" icon={<CheckCircleIcon />}>No directors flagged for removal.</Alert>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Discrepancies - To Update */}
              <Accordion defaultExpanded={validateResult.suggestions?.toUpdate?.length > 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EditIcon color="warning" />
                    <Typography variant="subtitle2">Data Discrepancies</Typography>
                    <Chip size="small" label={validateResult.suggestions?.toUpdate?.length || 0} color={validateResult.suggestions?.toUpdate?.length > 0 ? 'warning' : 'default'} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {Array.isArray(validateResult.suggestions?.toUpdate) && validateResult.suggestions.toUpdate.length > 0 ? (
                    <List dense disablePadding>
                      {validateResult.suggestions.toUpdate.map((u: any, idx: number) => (
                        <Paper key={`update-${idx}`} sx={{ p: 1.5, mb: 1, bgcolor: 'warning.50' }} variant="outlined">
                           <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} sx={{ mb: 1 }}>
                             <Typography variant="body2" fontWeight="bold" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>{u.name}</Typography>
                             <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                              {typeof u.matchScore === 'number' && u.matchScore < 1 && (
                                <Chip size="small" label={`Match: ${Math.round(u.matchScore * 100)}%`} variant="outlined" />
                              )}
                              {typeof u.confidence === 'number' && (
                                <Chip size="small" label={`${Math.round(u.confidence * 100)}%`} color={u.confidence >= 0.8 ? 'success' : 'warning'} />
                              )}
                              {u.hasGovernmentSource && (
                                <Tooltip title="Confirmed by government registry">
                                  <VerifiedUserIcon color="success" fontSize="small" />
                                </Tooltip>
                              )}
                            </Stack>
                          </Box>
                           {(u.diffs || []).map((df: any, di: number) => (
                             <Box key={di} sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 0.5, pl: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                              <Chip 
                                size="small" 
                                label={df.field} 
                                color={df.severity === 'critical' ? 'error' : df.severity === 'warning' ? 'warning' : 'default'}
                                sx={{ minWidth: 80 }}
                              />
                              <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
                                {df.currentValue ?? '—'}
                              </Typography>
                              <Typography variant="caption">→</Typography>
                              <Typography variant="caption" fontWeight="bold" color={df.severity === 'critical' ? 'error.main' : 'text.primary'}>
                                {df.registryValue ?? '—'}
                              </Typography>
                            </Box>
                          ))}
                          {u.notes && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                              {u.notes}
                            </Typography>
                          )}
                          {Array.isArray(u.sources) && u.sources.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                                {u.sources.map((s: any, si: number) => (
                                  <Chip
                                    key={si}
                                    size="small"
                                    label={s.name || (s.url ? new URL(s.url).hostname : 'Source')}
                                    component="a"
                                    href={s.url}
                                    target="_blank"
                                    clickable
                                    icon={s.isGovernment ? <VerifiedUserIcon /> : <LinkIcon />}
                                    color={s.isGovernment ? 'success' : 'default'}
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 22 }}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </List>
                  ) : (
                    <Alert severity="success" icon={<CheckCircleIcon />}>No data discrepancies found.</Alert>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Verified Directors */}
              {Array.isArray(validateResult.verified) && validateResult.verified.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon color="success" />
                      <Typography variant="subtitle2">Verified Directors</Typography>
                      <Chip size="small" label={validateResult.verified.length} color="success" />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense disablePadding>
                      {validateResult.verified.map((d: any, idx: number) => (
                        <Box key={`verified-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                           <Typography variant="body2" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>{d.name}</Typography>
                          {d.roleTitle && <Chip size="small" label={d.roleTitle} variant="outlined" sx={{ height: 20 }} />}
                          {typeof d.confidence === 'number' && (
                            <Chip size="small" label={`${Math.round(d.confidence * 100)}%`} color="success" variant="outlined" sx={{ height: 20 }} />
                          )}
                          {d.hasGovernmentSource && <VerifiedUserIcon color="success" fontSize="small" />}
                        </Box>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Sources Section */}
              <Accordion sx={{ mt: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SourceIcon />
                    <Typography variant="subtitle2">Data Sources</Typography>
                    <Chip size="small" label={validateResult.sources?.length || 0} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {Array.isArray(validateResult.sources) && validateResult.sources.length > 0 ? (
                    <List dense disablePadding>
                      {validateResult.sources.map((s: any, idx: number) => (
                        <ListItemButton 
                          key={`src-${idx}`} 
                          component="a" 
                          href={s.url} 
                          target="_blank" 
                          rel="noreferrer"
                          sx={{ borderRadius: 1, mb: 0.5 }}
                        >
                          {s.isGovernment ? (
                            <AccountBalanceIcon color="success" sx={{ mr: 1 }} />
                          ) : (
                            <PublicIcon color="action" sx={{ mr: 1 }} />
                          )}
                          <ListItemText
                            primary={s.name || new URL(s.url).hostname}
                            secondary={
                               <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                                {s.type && <Chip size="small" label={s.type.replace('_', ' ')} sx={{ height: 18, fontSize: '0.65rem' }} />}
                                {typeof s.confidence === 'number' && (
                                  <Chip size="small" label={`${(s.confidence * 100).toFixed(0)}% conf`} color={s.confidence >= 0.8 ? 'success' : 'default'} sx={{ height: 18, fontSize: '0.65rem' }} />
                                )}
                                {s.isGovernment && <Chip size="small" label="Official" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />}
                              </Stack>
                            }
                          />
                          <LaunchIcon fontSize="small" color="action" />
                        </ListItemButton>
                      ))}
                    </List>
                  ) : (
                    <Alert severity="info">No sources available.</Alert>
                  )}

                  {/* Curated Sources */}
                  {Array.isArray(validateResult.curatedSources) && validateResult.curatedSources.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                        Recommended Official Registries:
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {validateResult.curatedSources.map((s: any, idx: number) => (
                          <Chip
                            key={`cur-${idx}`}
                            size="small"
                            label={s.name}
                            component="a"
                            href={s.url}
                            target="_blank"
                            clickable
                            icon={<AccountBalanceIcon />}
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Analysis metadata */}
              {validateResult.analysisDate && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                  Analysis performed: {new Date(validateResult.analysisDate).toLocaleString()}
                </Typography>
              )}
            </Box>
          ) : (
            !validateLoading && <Alert severity="info" sx={{ mt: 2 }}>No validation result.</Alert>
          )}
              <Box sx={{ mt: 'auto', pt: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button onClick={() => setValidateOpen(false)} variant="outlined" fullWidth={isMobile}>Close</Button>
                </Stack>
              </Box>
            </Box>
          </Box>
        </>
      )}

      {/* Document Preview Dialog */}
      <DocumentPreviewDialog
        open={previewDialogOpen}
        onClose={() => {
          setPreviewDialogOpen(false);
          setPreviewDocumentId(null);
          setEditingDocument(null);
        }}
        documentId={previewDocumentId}
        documents={documents}
        currentIndex={previewDocumentIndex}
        onNavigate={handlePreviewNavigate}
      />
    </Container>
  );
};

export default Company;
